import { filter, last } from 'lodash';
import { Icon } from '@iconify/react';
import { sentenceCase } from 'change-case';
import { useState, useEffect } from 'react';
import plusFill from '@iconify/icons-eva/plus-fill';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { isAdminUser } from '../../utils/adminUserTypes';
// material
import { useTheme } from '@material-ui/core/styles';
import {
  Card,
  Table,
  Stack,
  Avatar,
  Button,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Tooltip
} from '@material-ui/core';
// redux
import { useDispatch, useSelector } from '../../redux/store';
import { getUserList, deleteUser } from '../../redux/slices/user';
// routes
import { PATH_DASHBOARD } from '../../routes/paths';
// components
import Page from '../../components/Page';
import Label from '../../components/Label';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import { UserListHead, UserListToolbar, UserMoreMenu } from '../../components/_dashboard/user/list';
import RequestToolbar from 'src/components/_dashboard/user/list/RequestToolbar';
import CustomerOrderMenu from 'src/components/_dashboard/user/list/CustomerOrderMenu.js';
import moment from 'moment';
import { COMPANY_NAME } from 'src/constants/Defaultvalues';
import { empty } from 'src/Helpers/Common';

// ----------------------------------------------------------------------
// Table head names same as DesignOrderList
const TABLE_HEAD = [
  { id: 'id', label: 'ID', alignRight: false },
  { id: 'name', label: 'Name', alignRight: false },
  { id: 'community_name', label: 'Project', alignRight: false },
  { id: 'customer_name', label: 'Client', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: 'options', label: 'Options', alignRight: false },
];
const TABLE_HEAD_TEAM_MEMBERS = [
  { id: 'id', label: 'ID', alignRight: false },
  { id: 'name', label: 'Name', alignRight: false },
  { id: 'community_name', label: 'Project', alignRight: false },
  { id: 'customer_name', label: 'Client', alignRight: false },
  { id: 'type', label: 'Request Type', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: 'options', label: 'Options', alignRight: false },
];

// ----------------------------------------------------------------------
// Alphanumeric-aware comparator (same style as DesignOrderList + numeric sort)
function descendingComparator(a, b, orderBy) {
  let aVal = a[orderBy];
  let bVal = b[orderBy];
  if (orderBy === 'status') {
    aVal = a.request_status ?? a.status ?? aVal;
    bVal = b.request_status ?? b.status ?? bVal;
  }
  const aStr = aVal != null ? String(aVal) : '';
  const bStr = bVal != null ? String(bVal) : '';
  const cmp = aStr.localeCompare(bStr, undefined, { numeric: true });
  if (cmp !== 0) return cmp > 0 ? -1 : 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Search by name, id, project (community_name), client (customer_name); then sort
function applySortFilter(array, comparator, query) {
  if (!array || !array.length) return [];
  let result = array;
  if (query && query.trim()) {
    const q = query.trim().toLowerCase();
    result = filter(array, (row) => {
      const name = (row.name || '').toLowerCase();
      const id = String(row.id ?? row.request_id ?? '').toLowerCase();
      const project = (row.community_name || '').toLowerCase();
      const client = (row.customer_name || '').toLowerCase();
      return name.indexOf(q) !== -1 || id.indexOf(q) !== -1 || project.indexOf(q) !== -1 || client.indexOf(q) !== -1;
    });
  }
  const stabilizedThis = result.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const onboard_data = [
  {
    id: 0,
    name: 'quote 1000',
    type: "RFQ",
    lastupdate: "2 Months ago",
    status: "Accepted",
    awarded: "Awarded"
  },
  {
    id: 1,
    name: 'Quote 101',
    type: "RFQ",
    lastupdate: "2 Months ago",
    status: "Accepted",
    awarded: "Awarded"
  },
  {
    id: 2,
    name: 'quote 100',
    type: "RFQ",
    lastupdate: "2 Months ago",
    status: "Accepted",
    awarded: "Awarded"
  },
  {
    id: 3,
    name: 'quote 11',
    type: "RFQ",
    lastupdate: "2 Months ago",
    status: "Accepted",
    awarded: "Awarded"
  },
]

const requestType = {
  1: "Assignment",
  2: "RFP",
  3: "RFI"
}


const requestState = {
  1: "Draft",
  2: "Open",
  3: "Evaluating",
  4: "Awarded",
  5: "Closed"
}


// Match DesignOrderList: Awarded/Completed states use success (green)
const requestColour = {
  1: "warning",   // Draft
  2: "info",      // Open
  3: "primary",   // Evaluating
  4: "success",   // Awarded (same as DesignOrderList completed/awarded = green)
  5: "error"      // Closed
}


const partnerStatus = {
  0: "Unseen",
  1: "Accepted",
  2: "Rejected"
}

// Same as DesignOrderList/OfficialRequest - job status label & color (e.g. "Installs Completed & Approved" in green)
function getStatusColorAndLabelSales(
  status,
  installationStatus,
  allInstallationCompleted,
  accepted,
  min_payment_status,
  billing_type,
  noServiceNoTask,
  proj_status,
  job_category_name,
  CompletedStatus,
  row,
  CommunityJob = false
) {
  if (status == null || status === undefined) return null;
  switch (Number(status)) {
    case 1:
      return { color: 'warning', label: 'Draft' };
    case 2:
      return { color: row?.hold == 1 ? 'warning' : 'info', label: row?.hold == 1 ? 'Hold' : 'Open' };
    case 3:
      return {
        color: row?.hold === 1 ? 'warning' : (row?.project_category == 13 ? 'primary' : min_payment_status == 0 ? 'warning' : 'primary'),
        label: row?.hold === 1 ? 'Hold' : (row?.project_category == 13 || billing_type == 0 ? 'Approved' : min_payment_status == 1 ? 'Approved and Paid' : 'Approved and Payment Due'),
      };
    case 4:
      return {
        color: (noServiceNoTask == 1 && proj_status == 3) ? 'success' : (empty(accepted) && !empty(allInstallationCompleted) ? 'warning' : 'success'),
        label: (noServiceNoTask == 1 && proj_status == 3) ? 'No Service No Task' :
          empty(accepted) && !empty(allInstallationCompleted) ? `${CompletedStatus} Completed & Awaiting approval` :
          (job_category_name === 'Build Order' && status === 4 && accepted === 1) ? 'Completed' :
          !empty(accepted) && !empty(allInstallationCompleted) ? `${CompletedStatus} Completed & Approved` :
          !empty(installationStatus) ? `${CompletedStatus}` : (CommunityJob ? `${CompletedStatus} Completed & Approved` : 'In-Production'),
      };
    case 5:
      return { color: 'warning', label: 'Closed' };
    case 6:
      return { color: 'error', label: 'Rejected' };
    case 10:
      return { color: 'success', label: 'Completed -No Service No Task' };
    default:
      return { color: 'default', label: 'Open' };
  }
}



export default function Request({ receivedId, requests, isTeam,isClient,clientId }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const Request = requests  // Replaced Request with onboard_data
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate()
  const userData = JSON.parse(localStorage.getItem('user'));
  let explodedPermissions = userData?.exploded_permissions

  if (isClient) {
    console.log("isTeam *********- ", isTeam)
    console.log('-------------------',requests)
    console.log("receivedId *******- ", receivedId)
  }


  useEffect(() => {
    dispatch(getUserList());
  }, [dispatch]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = Request?.map((n) => n.request_id||n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleDeleteUser = (userId) => {
    dispatch(deleteUser(userId));
  };

  const filteredUsers = applySortFilter(Request, getComparator(order, orderBy), filterName);
  const filteredCount = Math.max(0, Number(filteredUsers?.length) || 0);
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredCount) : 0;
  const isUserNotFound = filteredCount === 0;
  const pageCount = rowsPerPage > 0 ? Math.max(1, Math.ceil(filteredCount / rowsPerPage)) : 1;
  const safePage = Math.min(page, Math.max(0, pageCount - 1));

  // Keep page in bounds when data or rowsPerPage changes (avoids NaN in pagination)
  useEffect(() => {
    const maxPage = rowsPerPage > 0 ? Math.max(0, Math.ceil(filteredCount / rowsPerPage) - 1) : 0;
    if (page > maxPage) setPage(maxPage);
  }, [filteredCount, rowsPerPage, page]);

  return (
    <Page title={`Request |${COMPANY_NAME}`}>
      <Container>
        {/* <HeaderBreadcrumbs
          heading="User List"
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'User', href: PATH_DASHBOARD.user.root },
            { name: 'List' }
          ]}
          action={
            <Button
              variant="contained"
              component={RouterLink}
              to={PATH_DASHBOARD.user.newUser}
              startIcon={<Icon icon={plusFill} />}
            >
              New User
            </Button>
          }
        /> */}

        <Card>

          <RequestToolbar numSelected={selected.length} filterName={filterName} onFilterName={handleFilterByName} />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={isTeam ? TABLE_HEAD_TEAM_MEMBERS : TABLE_HEAD}
                  rowCount={filteredCount}
                  numSelected={selected?.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                <TableBody>
                  {filteredUsers?.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage).map((row) => {
                    const id = isTeam ? row.request_id : row.id;
                    const name = row.name;
                    const type = isTeam ? requestType[row.type] : row.type;
                    const isItemSelected = selected.indexOf(isTeam ? row.request_id : row.id) !== -1;
                    // Same as DesignOrderList: CompletedStatus for labels like "Installs Completed & Approved"
                    const CompletedStatus =
                      row?.installationCompleteStatus == 1 ? 'Installs' :
                      row?.installationCompleteStatus == 2 ? 'Pick up' :
                      row?.installationCompleteStatus == 3 ? 'Shipping' : '';
                    // Use job status (like OfficialRequest) when row has status; else request status
                    const salesStatus = getStatusColorAndLabelSales(
                      row?.status,
                      row?.installationStatus,
                      row?.allInstallationCompleted,
                      row?.installation_acceptance,
                      row?.min_payment_status,
                      row?.billing_type,
                      row?.noServiceNoTask,
                      row?.proj_status,
                      row?.job_category_name,
                      CompletedStatus,
                      row,
                      false
                    );
                    const statusLabel = salesStatus ? salesStatus.label : (requestState[row.request_status ?? row.status] ?? '-');
                    const statusColor = salesStatus ? salesStatus.color : (requestColour[row.request_status ?? row.status] ?? 'default');

                    return (
                      <TableRow
                        hover
                        key={id}
                        tabIndex={-1}
                        role="checkbox"
                        selected={isItemSelected}
                        aria-checked={isItemSelected}
                      >
                        <TableCell padding="checkbox">
                          {isAdminUser (userData.user_type) ? (
                            <Checkbox checked={isItemSelected} onChange={(e) => handleClick(e, id)} />
                          ) : null}
                        </TableCell>
                        <TableCell align="center">
                          <Typography component="span" sx={{ fontSize: '0.75rem', color: '#90EE90' }}>
                            {id ? `#${id}` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell component="th" scope="row" padding="none">
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Typography variant="subtitle2" noWrap sx={{ fontSize: '0.75rem' }}>
                              {name || '-'}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="left" sx={{ fontSize: '0.75rem' }}>
                          {row.community_name ? (
                            <Label
                              variant={theme.palette.mode === 'light' ? 'ghost' : 'filled'}
                              color="warning"
                            >
                              <Tooltip title={row.community_name}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontSize: '0.75rem',
                                    cursor: 'pointer',
                                    display: 'inline-block',
                                    maxWidth: 100,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {row.community_name}
                                  </span>
                                </Typography>
                              </Tooltip>
                            </Label>
                          ) : (
                            <Label
                              variant={theme.palette.mode === 'light' ? 'ghost' : 'filled'}
                              color="error"
                            >
                              No project
                            </Label>
                          )}
                        </TableCell>
                        <TableCell align="left" sx={{ fontSize: '0.75rem' }}>
                          <Label
                            variant={theme.palette.mode === 'light' ? 'ghost' : 'filled'}
                            color="secondary"
                          >
                            <Tooltip title={row.customer_name || ''}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontSize: '0.75rem',
                                  cursor: 'pointer',
                                  display: 'inline-block',
                                  maxWidth: 150,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {row.customer_name || '-'}
                                </span>
                              </Typography>
                            </Tooltip>
                          </Label>
                        </TableCell>
                        {isTeam && (
                          <TableCell align="center" sx={{ fontSize: '0.75rem' }}>{type || '-'}</TableCell>
                        )}
                        <TableCell align="left" sx={{ fontSize: '0.75rem' }}>
                          <Label
                            variant={theme.palette.mode === 'light' ? 'ghost' : 'filled'}
                            color={statusColor}
                          >
                            {statusLabel}
                          </Label>
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={!explodedPermissions?.some((p) => ['Create Request', 'Edit Request', 'Delete request', 'Add vendor to request', 'Add client to request'].includes(p))}
                            onClick={() => navigate(`/dashboard/official-request/view/${id}`)}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={isTeam ? 8 : 7} />
                    </TableRow>
                  )}
                </TableBody>
                {isUserNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={isTeam ? 8 : 7} sx={{ py: 3 }}>
                        <SearchNotFound searchQuery={filterName} msg="Requests" />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredCount}
            rowsPerPage={rowsPerPage}
            page={safePage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
    </Page>
  );
}
